import React, { useEffect, useContext } from 'react'
import { useFit } from '@helpers/hooks/useFit'
import { getAllAvailableProductOptionValues } from '@helpers/filters'
import { ORDERED_OPTIONS } from '@consts/index'
import SavedFitBox from '@components/SavedFitBox'
import Notification from '@components/ui/Notification'
import VariantFilter from './VariantFilter'
import QuickLinks from './QuickLinks'
import styles from './styles.module.scss'
import ProductContext from '../context'
import { ProductOption, ProductWithVariants } from 'types/product'
import { FilterOption } from 'types/filters'
import htmr from 'htmr'
import { resetFTV } from '@components/FitFinder/helpers'
import useABtests from '@helpers/hooks/useABtests'
import useBrowsingInNativeCurrency from '@helpers/hooks/useBrowsingInNativeCurrency'
import { FLAGS } from '@lib/flags'

const OPTION_VALUES_EXCLUSIONS = [{ type: 'build', value: 'X' }]

function getSavedFitOptionsToShow(productOptions: ProductOption[]) {
  return productOptions
    .map(option => {
      const hasExcludedValues = option.values.some(value =>
        OPTION_VALUES_EXCLUSIONS.find(
          excludedValue =>
            option.type === excludedValue.type && value === excludedValue.value
        )
      )
      if (!hasExcludedValues) {
        return option.type
      }
      return null
    })
    .filter(Boolean)
}

function getListOfOptionsPositions(
  productOptions: ProductOption[],
  filteredSelectedOptions: FilterOption[]
) {
  return ORDERED_OPTIONS.reduce<string[]>((result, optionType) => {
    const normalizedOptionType = optionType.toLowerCase()
    const optionExists =
      productOptions.findIndex(f => f.type === normalizedOptionType) > -1

    if (optionExists) {
      result.push(normalizedOptionType)
    }

    return result
  }, []).reduce<number[]>((result, optionType, optionIndex) => {
    const optionSelected =
      filteredSelectedOptions.findIndex(f => f.type === optionType) > -1

    if (optionSelected) {
      result.push(optionIndex + 1)
    }

    return result
  }, [])
}

function getMaxNumberOfOptionsToShow(listOfOptionsPositions: number[]) {
  return listOfOptionsPositions
    .sort((a, b) => (a > b ? 1 : -1))
    .reduce(
      (max, totalSelectedOptions) =>
        totalSelectedOptions === max + 1 ? totalSelectedOptions : max,
      0
    )
}

type FilterProps = {
  product: ProductWithVariants
  variantExist: boolean
  selectedOptions: FilterOption[]
  filterOptions: FilterOption[]
  hasSizeChartData?: boolean
}

function Filter({
  product,
  variantExist,
  selectedOptions,
  filterOptions,
  hasSizeChartData = true
}: FilterProps) {
  const { updateFilters } = useFit()
  const {
    options: productOptions,
    product_type: productType,
    product_feed: productFeed
  } = product
  const { discount } = useContext(ProductContext)
  const browsingInNativeCurrency = useBrowsingInNativeCurrency()
  const { testsVariants } = useABtests()
  const isVariant = testsVariants[FLAGS.PDP_SIZE_OPTIONS] === 'variant-1'

  const triggerSelectFilter = (payload: FilterOption[]) => {
    updateFilters(payload, selectedOptions)
  }

  useEffect(() => {
    resetFTV()
  }, [filterOptions, productType])

  const listOfOptionsPositions = getListOfOptionsPositions(
    productOptions,
    selectedOptions
  )
  const maxNumberOfOptionsToShow = getMaxNumberOfOptionsToShow(
    listOfOptionsPositions
  )

  const savedFitOptionsToShow = getSavedFitOptionsToShow(productOptions)

  return (
    <div className={styles.filters}>
      {!!productOptions.length && (
        <div className={styles.SavedFitBox__wrapper}>
          <SavedFitBox
            optionTypesToShow={savedFitOptionsToShow}
            appliedFilterOptions={selectedOptions}
          />
        </div>
      )}
      {productOptions.map((option, index) => {
        const displayIndex = index + 1
        return (
          <VariantFilter
            name={option.name}
            type={option.type}
            selectedOptions={selectedOptions}
            open={index <= maxNumberOfOptionsToShow}
            position={option.position}
            variantExist={variantExist}
            values={option.values}
            productType={productType}
            optionsList={getAllAvailableProductOptionValues(
              product,
              selectedOptions,
              option.type
            )}
            key={`${option.type}`}
            index={displayIndex}
            isVariant={isVariant}
            selectFilter={triggerSelectFilter}
          />
        )
      })}
      {browsingInNativeCurrency && !!discount.description && (
        <Notification
          type="info"
          content={htmr(`${discount.description}`, {
            dangerouslySetChildren: ['br']
          })}
          classes={styles.filters__MessageBox}
        />
      )}
      {hasSizeChartData && (
        <QuickLinks
          options={productOptions}
          type={productType}
          category={productFeed?.product_category || ''}
        />
      )}
    </div>
  )
}

export default Filter
